import type { Map as MlMap } from 'maplibre-gl';

/**
 * Elevation for the hole view.
 *
 * A top-down map destroys the single piece of information a golfer most wants
 * and cannot get by looking up: whether the shot is uphill. Two 150m approaches
 * are not the same shot if one climbs eight metres, and the flat satellite tile
 * gives no hint which is which. Pitching the camera without terrain does not
 * fix it either — that is a photograph on a tilted plane, and it reads as
 * exactly that. The relief is the product.
 *
 * Source is AWS's public terrain tiles (Terrarium encoding) for the same reason
 * basemap.ts leans on Esri and CARTO: free, no signup, no key to provision. The
 * app has to work the day somebody else installs it. If the operator later buys
 * a terrain licence, swap the source here and nothing else changes.
 *
 * 🟥 THE RESOLUTION CEILING — state this before demoing it. The underlying data
 * is ~30m-sample elevation. Fairway-scale relief (the 100–400m rise and fall
 * that decides club selection) renders honestly. **Green undulation does not**
 * — a green is about one sample wide, so it comes through as a smooth dome. Do
 * not let anyone believe this is reading the putting surface. Green contour is
 * a survey-grade product and a separate conversation.
 */

/** Terrarium tiles: elevation packed into RGB, decoded natively by MapLibre. */
const DEM_TILES = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

/** The DEM stops here; MapLibre overzooms past it rather than 404ing. */
const DEM_MAXZOOM = 15;

export const TERRAIN_SRC = 'src-dem';

/**
 * How hard to push the relief.
 *
 * Golf elevation is subtle in absolute terms — a hole that plays dramatically
 * uphill might rise ten metres over four hundred. At 1× that is a half-degree
 * slope and it is invisible at any camera angle a driver would tolerate; the
 * screen looks flat and the feature reads as broken. Exaggeration is what makes
 * a true reading legible rather than what makes a false one look good.
 *
 * 2.2 was chosen against the honest failure on both sides: below ~1.8 the rise
 * on a genuinely uphill hole cannot be seen, and above ~3 flat parkland courses
 * grow hills that are not there, which is worse than showing nothing. If the
 * client wants it dialled, this is the number.
 */
export const DEFAULT_EXAGGERATION = 2.2;

/**
 * Attach terrain to a live map.
 *
 * Safe to call more than once — MapLibre throws if a source id is added twice,
 * and style reloads re-fire `load`. Returns whether terrain ended up on, so the
 * caller can surface a real answer instead of assuming.
 */
export function applyTerrain(m: MlMap, exaggeration = DEFAULT_EXAGGERATION): boolean {
  try {
    if (!m.getSource(TERRAIN_SRC)) {
      m.addSource(TERRAIN_SRC, {
        type: 'raster-dem',
        tiles: [DEM_TILES],
        tileSize: 256,
        maxzoom: DEM_MAXZOOM,
        encoding: 'terrarium',
        attribution: 'Elevation © Mapzen / AWS Terrain Tiles',
      });
    }

    m.setTerrain({ source: TERRAIN_SRC, exaggeration });

    // Without a sky the pitched camera renders the horizon as flat page
    // background, which reads as a rendering fault rather than as distance.
    // The haze is doing the real work: it puts the far end of the hole *behind*
    // the near end instead of merely above it on screen.
    m.setSky({
      'sky-color': '#8fbfe8',
      'horizon-color': '#dfeaf2',
      'fog-color': '#e8eef2',
      'fog-ground-blend': 0.6,
      'horizon-fog-blend': 0.7,
      'sky-horizon-blend': 0.6,
      'atmosphere-blend': 0.8,
    });

    return true;
  } catch (err) {
    // Never silently flatten. A hole view that quietly loses its relief looks
    // like a design choice rather than a failure, and nobody reports it.
    console.warn('[terrain] could not attach elevation, hole view stays flat:', err);
    return false;
  }
}

/** Drop terrain but leave the source in place, so toggling back is instant. */
export function clearTerrain(m: MlMap): void {
  try {
    m.setTerrain(null);
  } catch {
    /* map already torn down */
  }
}
